import { useMemo } from "react";

import { useFormikWithDirtyForm } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { courtsApi } from "@/api";
import { withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { CourtFormType } from "../components/court-dialog/court-form-fields/CourtForm.types";
import {
  applyCourtSaveError,
  courtValidationSchema,
  emptyCourtFormValues,
  formValuesToRequest,
} from "../components/court-dialog/court-form-fields/CourtForm.utils";

export function useCourtCreation(onClose: () => void) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (values: CourtFormType) =>
      courtsApi.createCourt({ createCourtRequest: formValuesToRequest(values) }, withLocalErrorHandling),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Courts] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.AvailableCourtDistricts] });
      showSuccess(t("settings.courts.successfullyCreated"));
      onClose();
    },
  });

  const validationSchema = useMemo(() => courtValidationSchema(t), [t]);

  const formik = useFormikWithDirtyForm<CourtFormType>({
    initialValues: emptyCourtFormValues(),
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values, { setFieldError }) => {
      try {
        await createMutation.mutateAsync(values);
      } catch (error) {
        await applyCourtSaveError(error, setFieldError, showError, t);
      }
    },
  });

  return { formik };
}
